import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to WebForms! 👋',
    description: 'Build powerful forms in minutes. This quick tour will show you everything you need to get started — from creating your first form to collecting submissions.',
    icon: 'Sparkles',
    target: null,
    position: 'center',
    highlight: null,
    action: null,
    tip: 'Takes about 2 minutes',
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description: 'This is your command center. All your forms live here. You can search, filter, organize into folders, and track performance at a glance.',
    icon: 'LayoutDashboard',
    target: '[data-tour="dashboard-header"]',
    position: 'bottom',
    highlight: '[data-tour="dashboard-header"]',
    action: null,
    tip: 'Tip: Use folders to organize forms by project or client',
  },
  {
    id: 'create-form',
    title: 'Create a New Form',
    description: 'Click "Create New Form" to open the Form Builder. You can start from scratch or pick a template — drag and drop fields, configure settings, and publish in seconds.',
    icon: 'Plus',
    target: '[data-tour="create-form-btn"]',
    position: 'bottom',
    highlight: '[data-tour="create-form-btn"]',
    action: { label: 'Open Form Builder', path: '/form-builder' },
    tip: 'Tip: The builder supports 20+ field types including payments',
  },
  {
    id: 'templates',
    title: 'Start with a Template',
    description: 'Browse 24 ready-made templates across Contact Forms, Lead Generation, Surveys, Booking, Payment, and Registration. Each template is fully editable in the builder.',
    icon: 'LayoutTemplate',
    target: '[data-tour="sidebar-templates"]',
    position: 'right',
    highlight: '[data-tour="sidebar-templates"]',
    action: { label: 'Browse Templates', path: '/templates-gallery' },
    tip: 'Tip: Templates include pre-built field logic and styling',
  },
  {
    id: 'submissions',
    title: 'Manage Submissions',
    description: 'Every form response is captured here. Filter, label, export, and bulk-manage submissions so you can review and follow up on your own terms.',
    icon: 'Inbox',
    target: '[data-tour="sidebar-submissions"]',
    position: 'right',
    highlight: '[data-tour="sidebar-submissions"]',
    action: { label: 'View Submissions', path: '/submissions-management' },
    tip: 'Tip: Use labels to categorize and prioritize responses',
  },
  {
    id: 'analytics',
    title: 'Track Performance',
    description: 'See how your forms perform with real-time analytics — submission rates, device breakdowns, geographic distribution, and funnel analysis.',
    icon: 'BarChart3',
    target: '[data-tour="sidebar-analytics"]',
    position: 'right',
    highlight: '[data-tour="sidebar-analytics"]',
    action: { label: 'View Analytics', path: '/analytics' },
    tip: 'Tip: A/B test different form versions to optimize conversions',
  },
  {
    id: 'embed',
    title: 'Publish & Embed',
    description: 'Share your form via a direct link, embed it on any website with a script tag or iFrame, or use the React component for seamless integration.',
    icon: 'Code2',
    target: '[data-tour="sidebar-embed"]',
    position: 'right',
    highlight: '[data-tour="sidebar-embed"]',
    action: { label: 'Embed Settings', path: '/embed-settings' },
    tip: 'Tip: Use domain restrictions to control where forms can be embedded',
  },
  {
    id: 'done',
    title: "You\'re all set! 🎉",
    description: "You\'ve seen the core features. Start by creating your first form or picking a template. Need help? Visit the Help Center or API Docs anytime.",
    icon: 'CheckCircle2',
    target: null,
    position: 'center',
    highlight: null,
    action: { label: 'Create My First Form', path: '/form-builder' },
    tip: null,
  },
];

const STORAGE_KEY = 'webforms_onboarding_completed';
const STORAGE_DISMISSED_KEY = 'webforms_onboarding_dismissed';

// Spotlight overlay that highlights a DOM element
const Spotlight = ({ targetSelector }) => {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!targetSelector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(targetSelector);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el?.getBoundingClientRect();
    setRect({ top: r?.top, left: r?.left, width: r?.width, height: r?.height });

    const handleResize = () => {
      const updated = el?.getBoundingClientRect();
      setRect({ top: updated?.top, left: updated?.left, width: updated?.width, height: updated?.height });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [targetSelector]);

  if (!rect) return null;

  const pad = 8;
  return (
    <div className="fixed inset-0 pointer-events-none z-[1100]">
      {/* Top */}
      <div
        className="absolute bg-black/60"
        style={{ top: 0, left: 0, right: 0, height: rect?.top - pad }}
      />
      {/* Bottom */}
      <div
        className="absolute bg-black/60"
        style={{ top: rect?.top + rect?.height + pad, left: 0, right: 0, bottom: 0 }}
      />
      {/* Left */}
      <div
        className="absolute bg-black/60"
        style={{ top: rect?.top - pad, left: 0, width: rect?.left - pad, height: rect?.height + pad * 2 }}
      />
      {/* Right */}
      <div
        className="absolute bg-black/60"
        style={{ top: rect?.top - pad, left: rect?.left + rect?.width + pad, right: 0, height: rect?.height + pad * 2 }}
      />
      {/* Highlight ring */}
      <div
        className="absolute rounded-lg ring-2 ring-primary ring-offset-0"
        style={{
          top: rect?.top - pad,
          left: rect?.left - pad,
          width: rect?.width + pad * 2,
          height: rect?.height + pad * 2,
        }}
      />
    </div>
  );
};

// Tooltip card positioned relative to target element
const TooltipCard = ({ step, stepIndex, totalSteps, onNext, onPrev, onSkip, onNavigate, isFirst, isLast }) => {
  const [pos, setPos] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
  const cardRef = useRef(null);

  useEffect(() => {
    if (!step?.target || step?.position === 'center') {
      setPos({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }
    const el = document.querySelector(step?.target);
    if (!el) {
      setPos({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }
    const r = el?.getBoundingClientRect();
    const cardW = 360;
    const cardH = 280;
    const pad = 16;

    let style = {};
    if (step?.position === 'bottom') {
      let left = r?.left + r?.width / 2 - cardW / 2;
      left = Math.max(pad, Math.min(left, window.innerWidth - cardW - pad));
      style = { top: r?.bottom + 12, left, transform: 'none' };
    } else if (step?.position === 'right') {
      let top = r?.top + r?.height / 2 - cardH / 2;
      top = Math.max(pad, Math.min(top, window.innerHeight - cardH - pad));
      style = { top, left: r?.right + 12, transform: 'none' };
    } else if (step?.position === 'left') {
      let top = r?.top + r?.height / 2 - cardH / 2;
      top = Math.max(pad, Math.min(top, window.innerHeight - cardH - pad));
      style = { top, left: r?.left - cardW - 12, transform: 'none' };
    } else {
      style = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    setPos(style);
  }, [step]);

  const progressPct = ((stepIndex + 1) / totalSteps) * 100;

  return (
    <div
      ref={cardRef}
      className="fixed z-[1200] w-[360px] bg-card border border-border rounded-xl shadow-2xl"
      style={pos}
    >
      {/* Progress bar */}
      <div className="h-1 bg-muted rounded-t-xl overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-500 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name={step?.icon} size={18} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Step {stepIndex + 1} of {totalSteps}
              </p>
              <h3 className="text-sm font-heading font-semibold text-foreground leading-tight">
                {step?.title}
              </h3>
            </div>
          </div>
          <button
            onClick={onSkip}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
            aria-label="Close walkthrough"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground leading-relaxed mb-3">
          {step?.description}
        </p>

        {/* Tip */}
        {step?.tip && (
          <div className="flex items-start gap-2 bg-primary/5 border border-primary/15 rounded-lg px-3 py-2 mb-4">
            <Icon name="Lightbulb" size={14} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
            <p className="text-xs text-primary/90 leading-relaxed">{step?.tip}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            {!isFirst && (
              <button
                onClick={onPrev}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5 rounded hover:bg-muted"
              >
                <Icon name="ChevronLeft" size={14} />
                Back
              </button>
            )}
            <button
              onClick={onSkip}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5 rounded hover:bg-muted"
            >
              Skip tour
            </button>
          </div>

          <div className="flex items-center gap-2">
            {step?.action && !isLast && (
              <button
                onClick={() => onNavigate(step?.action?.path)}
                className="text-xs text-primary hover:text-primary/80 transition-colors font-medium flex items-center gap-1"
              >
                {step?.action?.label}
                <Icon name="ExternalLink" size={12} />
              </button>
            )}
            {isLast ? (
              <Button variant="default" size="sm" onClick={onSkip}>
                {step?.action ? step?.action?.label : 'Get Started'}
              </Button>
            ) : (
              <Button variant="default" size="sm" onClick={onNext} iconName="ChevronRight" iconPosition="right">
                Next
              </Button>
            )}
          </div>
        </div>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {Array.from({ length: totalSteps })?.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === stepIndex
                  ? 'w-4 h-1.5 bg-primary'
                  : i < stepIndex
                  ? 'w-1.5 h-1.5 bg-primary/40' :'w-1.5 h-1.5 bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const OnboardingWalkthrough = ({ onComplete, forceShow = false }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (forceShow) {
      setIsVisible(true);
      setCurrentStep(0);
      return;
    }
    const completed = localStorage.getItem(STORAGE_KEY);
    const dismissed = localStorage.getItem(STORAGE_DISMISSED_KEY);
    if (!completed && !dismissed) {
      // Small delay so dashboard renders first
      const timer = setTimeout(() => setIsVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, [forceShow]);

  const step = STEPS?.[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === STEPS?.length - 1;

  const handleNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => Math.min(prev + 1, STEPS?.length - 1));
      setIsAnimating(false);
    }, 150);
  }, [isAnimating]);

  const handlePrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => Math.max(prev - 1, 0));
      setIsAnimating(false);
    }, 150);
  }, [isAnimating]);

  const handleSkip = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsVisible(false);
    onComplete?.();
    // If last step has an action, navigate there
    if (isLast && step?.action) {
      navigate(step?.action?.path);
    }
  }, [isLast, step, navigate, onComplete]);

  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(STORAGE_DISMISSED_KEY, 'true');
    setIsVisible(false);
    onComplete?.();
  }, [onComplete]);

  if (!isVisible) return null;

  const isCentered = !step?.target || step?.position === 'center';

  return (
    <>
      {/* Backdrop */}
      {isCentered ? (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1100]"
          onClick={handleDismiss}
        />
      ) : (
        <Spotlight targetSelector={step?.highlight} />
      )}
      {/* Tooltip card */}
      <div
        className={`transition-opacity duration-150 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}
      >
        <TooltipCard
          step={step}
          stepIndex={currentStep}
          totalSteps={STEPS?.length}
          onNext={handleNext}
          onPrev={handlePrev}
          onSkip={handleSkip}
          onNavigate={handleNavigate}
          isFirst={isFirst}
          isLast={isLast}
        />
      </div>
    </>
  );
};

export default OnboardingWalkthrough;
export { STORAGE_KEY, STORAGE_DISMISSED_KEY };
